import React, { useState } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import NavbarMenu from "../../components/heroSection/NavbarMenu";
import {
  FaBuilding,
  FaUser,
  FaRoad,
  FaMapMarkerAlt,
  FaAddressCard,
  FaLock,
  FaEnvelope,
  FaEye, 
  FaEyeSlash,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { authAPI } from "../../api/apiSystem";
import { useNavigate } from "react-router-dom";
import "./RegisterDriverPage.css";
import Footer from "../footer/Footer";
import { useTranslation } from "react-i18next";
import SEO from "../SEO";

export default function RegisterDriverPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    nicname: "",
    company_name: "",
    email: "",
    password: "",
    street_address: "",
    addressone: "",
    postal_code: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    password: false,
  });

  const togglePassword = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, label: "", color: "#e5e7eb" };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2)
      return { level: 1, label: t("common.weak", "Weak"), color: "#dc2626" };
    if (score === 3)
      return { level: 2, label: t("common.medium", "Medium"), color: "#d97706" };
    if (score === 4)
      return { level: 3, label: t("common.strong", "Strong"), color: "#16a34a" };
    return { level: 4, label: t("common.veryStrong", "Very strong"), color: "#059669" };
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await authAPI.registerDriver(formData);

      if (response.success) {
        if (response.data?.access_token) {
          localStorage.setItem("token", response.data.access_token);
        }

        localStorage.setItem("confirm_email", formData.email);

        Swal.fire({
          icon: "success",
          title: t("RegisterDriverPage.success_message.title"),
          text: t("RegisterDriverPage.success_message.text"),
          confirmButtonColor: "#f97316",
          confirmButtonText: t(
            "RegisterDriverPage.success_message.confirm_button",
          ),
        }).then(() => {
          navigate("/otp-verification");
        });

        setFormData({
          firstname: "",
          lastname: "",
          nicname: "",
          company_name: "",
          email: "",
          password: "",
          street_address: "",
          addressone: "",
          postal_code: "",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: t("RegisterDriverPage.error_message.title"),
          text: response.message || t("RegisterDriverPage.error_message.text"),
          confirmButtonColor: "#f97316",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("RegisterDriverPage.error_message.title"),
        text:
          error.message || t("RegisterDriverPage.error_message.server_error"),
        confirmButtonColor: "#f97316",
      });
    }
  };

  return (
    <>
      <SEO
        title="TransCirox – Als Fahrer registrieren | Transportpartner werden"
        description="Registrieren Sie sich als Fahrer oder Transportunternehmen bei TransCirox und erhalten Sie Aufträge für Umzüge und Transporte."
        keywords="Fahrer registrieren, Transportpartner werden, Transportunternehmen anmelden, Transport Job"
      />
      <NavbarMenu />

      <div
        className="register-driver-section"
        style={{ minHeight: "100vh" }}
      >
        <Container>
          <Row className="justify-content-center align-items-center h-100">
            <Col md={10} lg={8}>
              <Card className="shadow-lg p-4 border-0 dark-register-driver-card">
                <Card.Body>
                  <h3 className="text-center mb-4 text-dark fw-bold">
                    {t("RegisterDriverPage.form_title")}
                  </h3>

                  <Form onSubmit={handleSubmit}>
                    <Row className="g-3">
                      <Col md={12}>
                        <Form.Group controlId="formCompany">
                          <Form.Label>
                            {t("RegisterDriverPage.form_fields.company_name")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaBuilding className="form-icon" />
                            <Form.Control
                              type="text"
                              name="company_name"
                              value={formData.company_name}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterDriverPage.form_fields.placeholders.company_name",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formFirstName">
                          <Form.Label>
                            {t("RegisterDriverPage.form_fields.first_name")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaUser className="form-icon" />
                            <Form.Control
                              type="text"
                              name="firstname"
                              value={formData.firstname}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterDriverPage.form_fields.placeholders.first_name",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formLastName">
                          <Form.Label>
                            {t("RegisterDriverPage.form_fields.last_name")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaUser className="form-icon" />
                            <Form.Control
                              type="text"
                              name="lastname"
                              value={formData.lastname}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterDriverPage.form_fields.placeholders.last_name",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formNicname">
                          <Form.Label>
                            {t("RegisterDriverPage.form_fields.nickname")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaUser className="form-icon" />
                            <Form.Control
                              type="text"
                              name="nicname"
                              value={formData.nicname}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterDriverPage.form_fields.placeholders.nickname",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formStreet">
                          <Form.Label>
                            {t("RegisterDriverPage.form_fields.street_address")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaRoad className="form-icon" />
                            <Form.Control
                              type="text"
                              name="street_address"
                              value={formData.street_address}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterDriverPage.form_fields.placeholders.street_address",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formAddition">
                          <Form.Label>
                            {t("RegisterDriverPage.form_fields.address_one")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaAddressCard className="form-icon" />
                            <Form.Control
                              type="text"
                              name="addressone"
                              value={formData.addressone}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterDriverPage.form_fields.placeholders.address_one",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formCityZip">
                          <Form.Label>
                            {t("RegisterDriverPage.form_fields.postal_code")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaMapMarkerAlt className="form-icon" />
                            <Form.Control
                              type="text"
                              name="postal_code"
                              value={formData.postal_code}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterDriverPage.form_fields.placeholders.postal_code",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formEmail">
                          <Form.Label>
                            {t("RegisterDriverPage.form_fields.email")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaEnvelope className="form-icon" />
                            <Form.Control
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterDriverPage.form_fields.placeholders.email",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formPassword">
                          <Form.Label>
                            {t("RegisterDriverPage.form_fields.password")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaLock className="form-icon" />
                            <Form.Control
                              type={showPasswords.password ? "text" : "password"}
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterDriverPage.form_fields.placeholders.password",
                              )}
                              className="ps-5 dark-bg-dark dark-text-white border-secondary password-input"
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() => togglePassword("password")}
                              tabIndex={-1}
                              aria-label={
                                showPasswords.password
                                  ? t("common.hidePassword", "Hide password")
                                  : t("common.showPassword", "Show password")
                              }
                            >
                              {showPasswords.password ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>

                          {formData.password && (
                            <div className="mt-2">
                              <div className="d-flex justify-content-between mb-1">
                                <small className="text-muted">{t("common.passwordStrength", "Password strength")}</small>
                                <small style={{ color: getPasswordStrength(formData.password).color, fontWeight: 600 }}>
                                  {getPasswordStrength(formData.password).label}
                                </small>
                              </div>
                              <div className="password-strength-bar">
                                <div
                                  className="password-strength-fill"
                                  style={{
                                    width: `${(getPasswordStrength(formData.password).level / 4) * 100}%`,
                                    backgroundColor: getPasswordStrength(formData.password).color,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </Form.Group>
                      </Col>

                      <Col xs={12} className="mt-3">
                        <Button
                          type="submit"
                          variant="warning"
                          className="w-100 bg-orange-500 text-white"
                        >
                          {t("RegisterDriverPage.button.submit")}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </>
  );
}
